import { useState } from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, RadialBarChart, RadialBar, PolarAngleAxis, LabelList,
} from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import {
  AlertTriangle, TrendingDown, TrendingUp, Lightbulb, Search, Star, Target,
  Info, ArrowRight, ShieldQuestion, Sparkles, GitCompare, Layers, ScrollText,
  Fingerprint, Scale, CheckCircle2, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import Narrativa from "./Narrativa";
import ProgressiStorico from "./ProgressiStorico";
import Materialita from "./Materialita";
import Confronto from "./Confronto";
import ConfrontoVoti from "./ConfrontoVoti";
import Pipeline from "./Pipeline";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TT } from "./ChartKit";


const COV = {
  HEALTHY: { label: "Quadro completo", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  DEGRADED_BUT_USABLE: { label: "Quadro parziale, ma affidabile", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  PARTIAL_BUT_VALID: { label: "Quadro parziale", cls: "bg-yellow-50 text-yellow-800 border-yellow-300" },
  BLOCKED_TECHNICALLY: { label: "Dati insufficienti", cls: "bg-red-50 text-red-700 border-red-300" },
};
const PRI = {
  CRITICAL: { label: "Critica", cls: "bg-red-100 text-red-700 border-red-300" },
  HIGH: { label: "Alta", cls: "bg-orange-100 text-orange-700 border-orange-300" },
  MEDIUM: { label: "Media", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  LOW: { label: "Bassa", cls: "bg-stone-100 text-stone-600 border-stone-300" },
};
const baseOf = (f) => (f.evidence_base ? `Basata su ${f.evidence_base}` : "Basata sulle fonti lette");
const DIM_LABEL = { pagine_lette: "Pagine del sito lette", nome: "Nome", posizionamento: "Posizionamento", promessa: "Promessa", target: "Pubblico", tono: "Tono di voce", valori: "Valori", servizi: "Servizi", prezzo: "Fascia di prezzo", rating: "Voto medio", recensioni: "Recensioni lette", fonti: "Fonti", lingue: "Lingue", canali: "Canali", stile: "Stile", parole_chiave: "Parole chiave", punti_forza: "Punti di forza", criticita: "Criticità" };
const LAYER = {
  declared: { label: "Dichiarata", hint: "Sito ufficiale, brand book, comunicazione" },
  digital_observed: { label: "Digitale osservata", hint: "Solo da UX/contenuti/foto/SEO" },
  perceived: { label: "Percepita", hint: "Solo da recensioni, OTA, articoli" },
  strategic_desired: { label: "Strategica desiderata", hint: "Solo dal direttore" },
};

const Overline = ({ children }) => (
  <div className="text-brand-orange font-bold tracking-[0.22em] text-[11px] uppercase">{children}</div>
);
const Card = ({ className = "", children, ...p }) => (
  <div className={`bg-white rounded-xl border border-brand-gold/25 shadow-[0_1px_2px_rgba(15,58,45,0.04)] ${className}`} {...p}>{children}</div>
);
const IconBadge = ({ icon: Icon, className = "" }) => (
  <div className={`w-12 h-12 rounded-full bg-brand-green flex items-center justify-center shrink-0 ${className}`}>
    <Icon size={22} className="text-brand-gold" strokeWidth={1.75} />
  </div>
);

export default function Analisi({ ccu, scope, reload, onNav }) {
  const [explain, setExplain] = useState({ open: false, field: null });
  const openExplain = (field = null) => setExplain({ open: true, field });

  const cs = ccu.cognitive_state;
  const d = ccu.delta_vs_previous;
  const attention = d.new_criticalities.length + cs.open_contradictions.length > 0;

  return (
    <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-5 lg:py-7 max-w-[1320px] 2xl:max-w-[1560px] min-[2000px]:max-w-[1760px] leu-in">
      <Pipeline scope={scope} onDone={reload} />
      <Hero ccu={ccu} attention={attention} />
      <StatCards cs={cs} d={d} />
      <Narrativa ccu={ccu} scope={scope} />
      <ProgressiStorico scope={scope} />
      <IdentityLayers ccu={ccu} openExplain={openExplain} />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-8 [&>*]:min-w-0">
        <div className="xl:col-span-2"><CoherenceMatrix matrix={ccu.identity_coherence_matrix} /></div>
        <StatoComplessivo cs={cs} />
      </div>
      {cs.open_contradictions.length > 0 && (
        <Contradictions items={cs.open_contradictions} scope={scope} reload={reload} openExplain={openExplain} />
      )}
      <Diagrammi ccu={ccu} />
      <CosaEmerge cs={cs} images={ccu.canonical_identity?.images || []} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <Findings items={cs.active_findings_snapshot} />
        <Confronto ccu={ccu} scope={scope} />
      </div>
      <div className="mb-8"><ConfrontoVoti scope={scope} /></div>
      <Materialita ccu={ccu} scope={scope} reload={reload} />
      <Positioning positioning={ccu.positioning} />
      <Actions ccu={ccu} scope={scope} reload={reload} openExplain={openExplain} />
      <Provenance ccu={ccu} />
      <ExplainSheet ccu={ccu} state={explain} onOpenChange={(o) => setExplain((s) => ({ ...s, open: o }))} />
    </div>
  );
}

/* ---------------- Hero ---------------- */
function Hero({ ccu, attention }) {
  const cov = COV[ccu.cognitive_state.coverage_status.level] || COV.BLOCKED_TECHNICALLY;
  const when = ccu.computed_at ? new Date(ccu.computed_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
  const hero = ccu.canonical_identity?.hero_image;
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6 mb-8">
      <div>
        <Overline>Panoramica</Overline>
        <div className="flex items-start gap-4 mt-3">
          <IconBadge icon={attention ? AlertTriangle : CheckCircle2} />
          <div>
            <h2 className="font-serif text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.1] text-brand-ink">
              {attention ? "C'è qualcosa che richiede la mia attenzione?" : "Ecco come sta andando il tuo hotel."}
            </h2>
            <p className="text-[14.5px] text-brand-muted mt-2 max-w-2xl">
              La comprensione consolidata di LEUTELIA su <strong>{ccu.canonical_identity?.name}</strong> adesso: un'unica lettura affidabile, con le informazioni più importanti subito.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-4 text-[12px] font-mono">
              <span className="px-2.5 py-1 rounded-full bg-brand-green text-brand-cream font-semibold" data-testid="ccu-version">
                Versione {ccu.understanding_version}
              </span>
              <span className={`px-2.5 py-1 rounded-full border font-semibold ${cov.cls}`} data-testid="coverage-badge">{cov.label}</span>
              <span className="text-brand-muted">calcolato {when}</span>
              
            </div>
          </div>
        </div>
      </div>
      <Card className="overflow-hidden relative min-h-[168px] bg-brand-green" data-testid="hotel-hero">
        {hero && <img src={hero} alt={ccu.canonical_identity?.name || ""} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-greenDark/85 via-brand-green/40 to-transparent" />
        <div className="relative p-5 h-full flex flex-col justify-end">
          <div className="border-l-4 border-brand-gold pl-3">
            <p className="font-serif italic text-[17px] text-brand-cream leading-snug">"Le grandi esperienze iniziano dai dettagli."</p>
            <p className="text-[12px] text-brand-cream/80 mt-1.5">{ccu.canonical_identity?.name} · più consapevolezza, ospiti più soddisfatti.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ---------------- Stat cards ---------------- */
function StatCards({ cs, d }) {
  const peggior = d.confidence_changes.filter((c) => c.direction === "down").length;
  const miglior = d.confidence_changes.filter((c) => c.direction === "up").length + d.resolved_criticalities.length + d.resolved_contradictions.length;
  const high = cs.active_findings_snapshot.filter((f) => ["CRITICAL", "HIGH"].includes(f.priority_category)).length;
  const cards = [
    { label: "Nuovi problemi", value: d.new_criticalities.length, icon: AlertTriangle, color: "#B0452E" },
    { label: "Peggioramenti", value: peggior, icon: TrendingDown, color: "#C2662E" },
    { label: "Miglioramenti", value: miglior, icon: TrendingUp, color: "#2F6B4F" },
    { label: "Opportunità", value: cs.opportunities.length, icon: Lightbulb, color: "#C6902A" },
    { label: "Da verificare", value: cs.open_contradictions.length, icon: Search, color: "#6F6A5F" },
    { label: "Priorità alte", value: high, icon: Star, color: "#C6902A" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-8">
      {cards.map((c) => (
        <Card key={c.label} className="px-4 py-4" data-testid={`stat-${c.label}`}>
          <div className="flex items-center gap-3">
            <c.icon size={22} style={{ color: c.color }} strokeWidth={1.9} />
            <div>
              <div className="font-serif text-[26px] leading-none text-brand-ink">{c.value}</div>
              <div className="text-[12px] text-brand-muted mt-1">{c.label}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- 4 Identità ---------------- */
function IdentityLayers({ ccu, openExplain }) {
  const layers = ccu.identity_layers || {};
  return (
    <section className="mb-8">
      <SectionHead icon={Fingerprint} title="Le quattro identità" sub="Come l'hotel si dichiara, come appare, come è percepito e dove vuole andare." />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {["declared", "digital_observed", "perceived", "strategic_desired"].map((k) => {
          const l = layers[k];
          const meta = LAYER[k];
          return (
            <Card key={k} className="p-5" data-testid={`identity-${k}`}>
              <div className="flex items-center justify-between">
                <div className="text-[12.5px] font-bold text-brand-green uppercase tracking-wide">{meta.label}</div>
                {l && <span className="text-[11px] font-mono text-brand-muted">{Math.round(l.source_confidence * 100)}%</span>}
              </div>
              <div className="text-[10.5px] text-brand-muted mt-0.5 mb-3">{meta.hint}</div>
              {l ? (
                <div className="space-y-2">
                  {Object.entries(l.dimensions).map(([dim, val]) => (
                    <div key={dim} className="text-[13px]">
                      <span className="text-brand-muted">{DIM_LABEL[dim] || dim.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())}: </span>
                      <span className="text-brand-ink font-medium">{val}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[13px] italic text-brand-muted py-2">
                  {k === "strategic_desired" ? "Non ancora dichiarata dal direttore." : "Nessuna evidenza disponibile."}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------- Coherence matrix ---------------- */
function CoherenceMatrix({ matrix }) {
  const Row = ({ dim, values, tone, note }) => (
    <div className="flex items-start gap-3 px-4 py-3 border-t border-brand-gold/15 hover:bg-brand-cream/60 transition-colors">
      <span className={`mt-1 w-2 h-2 rounded-full shrink-0`} style={{ background: tone }} />
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-brand-ink capitalize">{dim}</div>
        <div className="text-[12.5px] text-brand-muted">
          {Object.entries(values).map(([layer, v]) => (
            <span key={layer} className="mr-3"><span className="font-mono text-[11px]">{layer}</span>: {v}</span>
          ))}
        </div>
        {note && <div className="text-[11.5px] text-brand-orange mt-0.5">{note}</div>}
      </div>
    </div>
  );
  return (
    <Card>
      <div className="px-5 py-4">
        <SectionHead icon={Scale} title="Coerenza delle identità" sub="Dove le quattro identità concordano, dove no, e quanto sei lontano da dove vuoi arrivare." inline />
      </div>
      <div className="pb-3">
        {matrix.convergences.map((e, i) => <Row key={`c${i}`} dim={e.dimension} values={e.values} tone="#2F6B4F" note="Convergenza tra le identità" />)}
        {matrix.divergences.map((e, i) => <Row key={`d${i}`} dim={e.dimension} values={e.values} tone="#C2662E" note={e.note} />)}
        {matrix.gaps.map((e, i) => <Row key={`g${i}`} dim={e.dimension} values={e.values} tone="#C6902A" note={`${e.note} (opportunità)`} />)}
        {matrix.convergences.length + matrix.divergences.length + matrix.gaps.length === 0 && (
          <div className="px-5 py-6 text-[13px] text-brand-muted italic">Nessuna relazione tra identità da mostrare.</div>
        )}
      </div>
    </Card>
  );
}

/* ---------------- Stato complessivo (donut) ---------------- */
function StatoComplessivo({ cs }) {
  const data = [
    { name: "Criticità", value: cs.criticalities.length, color: "#B0452E" },
    { name: "Da verificare", value: cs.open_contradictions.length, color: "#C2662E" },
    { name: "Segnalazioni", value: cs.active_findings_snapshot.length, color: "#16493A" },
    { name: "Opportunità", value: cs.opportunities.length, color: "#C6902A" },
    { name: "Punti di forza", value: cs.strengths.length, color: "#7BA890" },
  ];
  const total = data.reduce((a, b) => a + b.value, 0);
  return (
    <Card className="p-5">
      <SectionHead icon={Layers} title="Stato complessivo" sub="Cosa abbiamo capito finora, in numeri." inline />
      <div className="flex flex-wrap items-center gap-4 mt-3">
        <div className="relative w-[130px] h-[130px] shrink-0">
          <ResponsiveContainer width={130} height={130}>
            <PieChart>
              <Pie data={total ? data : [{ name: "-", value: 1, color: "#E7E1D3" }]} dataKey="value" innerRadius={44} outerRadius={62} paddingAngle={2} stroke="none">
                {(total ? data : [{ color: "#E7E1D3" }]).map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-serif text-[24px] text-brand-ink leading-none">{total}</div>
            <div className="text-[10px] text-brand-muted">elementi</div>
          </div>
        </div>
        <div className="space-y-1.5 flex-1 min-w-[150px]">
          {data.map((e) => (
            <div key={e.name} className="flex items-center justify-between text-[12.5px]">
              <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: e.color }} />{e.name}</span>
              <span className="font-mono text-brand-ink">{e.value}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ---------------- Contraddizioni ---------------- */
function Contradictions({ items, scope, reload, openExplain }) {
  return (
    <section className="mb-8">
      <SectionHead icon={ShieldQuestion} title="Contraddizioni aperte" sub="Fatti su cui le fonti non concordano. LEUTELIA non sceglie da sola: serve la tua conferma." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((oc) => <ContradictionCard key={oc.id} oc={oc} scope={scope} reload={reload} openExplain={openExplain} />)}
      </div>
    </section>
  );
}
function ContradictionCard({ oc, scope, reload, openExplain }) {
  const candidates = Array.from(new Set((oc.description.match(/\d+(?:[.,]\d+)?/g) || [])));
  const [value, setValue] = useState(candidates[0] || "");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (value === "") return;
    setBusy(true);
    try {
      const num = Number(value);
      await api.confirmContradiction({ tenant: scope.tenant, hotel: scope.hotel, field: oc.field, value: Number.isNaN(num) ? value : num });
      toast.success(`Confermato ${oc.field} = ${value}. comprensione aggiornata.`);
      await reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 border-red-300 bg-red-50/40" data-testid={`contradiction-${oc.field}`}>
      <div className="flex items-center justify-between">
        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300 text-[11px] font-bold font-mono">CONTRADICTED</span>
        <button onClick={() => openExplain(oc.field)} className="text-[12px] text-brand-green hover:text-brand-gold font-semibold flex items-center gap-1" data-testid={`explain-${oc.field}`}>
          <Info size={13} /> Spiega
        </button>
      </div>
      <div className="font-serif text-[17px] text-brand-ink mt-2 capitalize">{oc.field.replace(/_/g, " ")}</div>
      <p className="text-[13px] text-brand-muted mt-1">{oc.description}</p>
      <div className="flex items-center gap-2 mt-3">
        {candidates.map((c) => (
          <button key={c} onClick={() => setValue(c)} data-testid={`cand-${oc.field}-${c}`}
            className={`px-3 py-1.5 rounded-lg border text-[13px] font-mono ${value === c ? "bg-brand-green text-brand-cream border-brand-green" : "bg-white border-brand-border text-brand-ink"}`}>
            {c}
          </button>
        ))}
        <Input value={value} onChange={(e) => setValue(e.target.value)} className="h-9 w-24 text-[13px]" data-testid={`confirm-input-${oc.field}`} />
        <Button onClick={confirm} disabled={busy} data-testid={`confirm-btn-${oc.field}`}
          className="h-9 bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13px] font-semibold">
          Conferma
        </Button>
      </div>
    </Card>
  );
}

/* ---------------- Diagrammi ---------------- */
function Diagrammi({ ccu }) {
  const cs = ccu.cognitive_state;
  const layers = ccu.identity_layers || {};
  const idConf = Math.round((ccu.canonical_identity?.identity_confidence || 0) * 100);
  const layerData = ["declared", "digital_observed", "perceived", "strategic_desired"].map((k) => ({
    name: LAYER[k].label, conf: layers[k] ? Math.round(layers[k].source_confidence * 100) : 0,
  }));
  const findData = cs.active_findings_snapshot.map((f) => ({
    name: f.title.length > 20 ? f.title.slice(0, 20) + "…" : f.title,
    conf: Math.round(f.conclusion_confidence * 100),
    color: (PRI[f.priority_category] || PRI.LOW), 
  }));
  const priColor = { CRITICAL: "#B0452E", HIGH: "#C2662E", MEDIUM: "#C6902A", LOW: "#9C968A" };
  const findBars = cs.active_findings_snapshot.map((f) => ({
    name: f.title.length > 22 ? f.title.slice(0, 22) + "…" : f.title,
    conf: Math.round(f.conclusion_confidence * 100),
    fill: priColor[f.priority_category] || priColor.LOW,
  }));

  return (
    <section className="mb-8">
      <SectionHead icon={Layers} title="Diagrammi e metriche" sub="Le informazioni sull'hotel in grafici, per una lettura immediata." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 [&>*]:min-w-0">
        {/* Gauge identity confidence */}
        <Card className="p-5 flex flex-col">
          <div className="text-[13px] font-semibold text-brand-ink">Quanto siamo sicuri di chi sei</div>
          <div className="text-[11.5px] text-brand-muted mb-1">Quanto le informazioni su nome, identità e promessa dell'hotel sono confermate dalle fonti.</div>
          <div className="relative h-[170px]">
            <ResponsiveContainer width="100%" height={170} minWidth={120}>
              <RadialBarChart innerRadius="72%" outerRadius="100%" data={[{ name: "id", value: idConf, fill: "#0F3A2D" }]} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar dataKey="value" cornerRadius={20} background={{ fill: "#EDE7D8" }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-serif text-[32px] text-brand-ink leading-none">{idConf}%</div>
              <div className="text-[11px] text-brand-muted">consolidata</div>
            </div>
          </div>
        </Card>

        {/* Confidenza per identità */}
        <Card className="p-5">
          <div className="text-[13px] font-semibold text-brand-ink">Quanto conosciamo bene l'hotel</div>
          <div className="text-[11.5px] text-brand-muted mb-3">Per ciascuna delle quattro viste, quanto sono complete le informazioni raccolte (100% = tutto verificato).</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={layerData} layout="vertical" margin={{ left: 8, right: 24 }}>
              <XAxis type="number" domain={[0, 100]} hide />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: "#6F6A5F" }} axisLine={false} tickLine={false} />
              <Tooltip {...TT} cursor={{ fill: "#F5F1E8" }} formatter={(v) => [`${v}%`, "informazioni verificate"]} />
              <Bar dataKey="conf" fill="#C6902A" radius={[0, 6, 6, 0]} barSize={16}>
                <LabelList dataKey="conf" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 11, fill: "#211F1A" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Confidenza finding */}
        <Card className="p-5">
          <div className="text-[13px] font-semibold text-brand-ink">Su cosa si basa ogni segnalazione</div>
          <div className="text-[11.5px] text-brand-muted mb-3">Ogni riga è un problema trovato. Sotto c'è scritto da quali dati reali nasce: più pagine, foto o recensioni sono state lette, più la segnalazione è solida (barra piena = molto solida). Il colore dice quanto è urgente.</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] text-brand-muted mb-2" data-testid="findings-legend">
            {[["CRITICAL", "da fare subito"], ["HIGH", "urgente"], ["MEDIUM", "importante"], ["LOW", "quando possibile"]].map(([k, l]) => <span key={k} className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: priColor[k] }} /> {l}</span>)}
          </div>
          {findBars.length ? (
            <ul className="divide-y divide-brand-gold/15 max-h-[260px] overflow-auto pr-1" data-testid="findings-confidence-list">
              {cs.active_findings_snapshot.map((f, i) => {
                const conf = Math.round(f.conclusion_confidence * 100), col = priColor[f.priority_category] || priColor.LOW;
                return (
                  <li key={f.id || i} className="py-2" data-testid={`finding-conf-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-[12.5px] text-brand-ink leading-snug">{f.module_label || f.module ? <span className="text-brand-muted">{f.module_label || f.module}: </span> : null}{f.title}</div>
                    </div>
                    <div className="text-[11px] text-brand-muted mt-0.5">{baseOf(f)}</div>
                    <div className="mt-1 h-1.5 rounded-full bg-brand-cream"><div className="h-1.5 rounded-full" style={{ width: `${conf}%`, background: col }} /></div>
                  </li>
                );
              })}
            </ul>
          ) : <div className="text-[13px] text-brand-muted italic py-6">Nessuna segnalazione rilevante al momento.</div>}
        </Card>
      </div>
    </section>
  );
}

/* ---------------- Cosa emerge ---------------- */
function CosaEmerge({ cs, images = [] }) {
  const items = [
    cs.criticalities[0] && { tone: "PROBLEMA", color: "#B0452E", icon: AlertTriangle, img: images[1], title: cs.criticalities[0].text },
    cs.opportunities[0] && { tone: "OPPORTUNITÀ", color: "#2F6B4F", icon: Lightbulb, img: images[2], title: cs.opportunities[0].text },
    (cs.open_contradictions[0] || cs.active_findings_snapshot[0]) && {
      tone: "DA VERIFICARE", color: "#6F6A5F", icon: Search, img: images[3] || images[0],
      title: cs.open_contradictions[0]?.field?.replace(/_/g, " ") || cs.active_findings_snapshot[0]?.title,
    },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <section className="mb-8">
      <SectionHead icon={Sparkles} title="Cosa emerge adesso" sub="Una lettura rapida delle cose più importanti emerse finora." />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <Card key={i} className="overflow-hidden flex flex-col">
            <div className="flex gap-4 p-5 flex-1">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: it.color }}>
                    <it.icon size={16} className="text-white" />
                  </span>
                  <span className="text-[11px] font-bold tracking-[0.16em]" style={{ color: it.color }}>{it.tone}</span>
                </div>
                <div className="font-serif text-[17px] text-brand-ink mt-3 leading-snug capitalize">{it.title}</div>
              </div>
              {it.img && <img src={it.img} alt="" className="w-24 h-24 object-cover rounded-lg shrink-0 hidden xl:block" />}
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Findings ---------------- */
function Findings({ items }) {
  return (
    <Card className="p-5">
      <SectionHead icon={Target} title="Segnalazioni che contano" sub="Solo le segnalazioni importanti e ancora aperte." inline />
      <div className="mt-3 divide-y divide-brand-gold/15">
        {items.length === 0 && <div className="text-[13px] text-brand-muted italic py-3">Nessuna segnalazione rilevante al momento.</div>}
        {items.map((f) => {
          const p = PRI[f.priority_category] || PRI.LOW;
          return (
            <div key={f.finding_id} className="flex items-center gap-3 py-3" data-testid={`finding-${f.finding_id}`}>
              <span className={`px-2 py-0.5 rounded-full border text-[10.5px] font-bold ${p.cls}`}>{p.label}</span>
              <span className="text-[13.5px] text-brand-ink flex-1">{f.title}</span>
              <span className="text-[11px] text-brand-muted text-right max-w-[220px] leading-snug">{baseOf(f)}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ---------------- Positioning ---------------- */
function Positioning({ positioning }) {
  const p = positioning;
  const Field = ({ label, value }) => value ? (
    <div><div className="text-[11px] font-bold uppercase tracking-wide text-brand-muted">{label}</div><div className="text-[14px] text-brand-ink mt-0.5">{value}</div></div>
  ) : null;
  return (
    <section className="mb-8">
      <SectionHead icon={ScrollText} title="Come si posiziona il tuo hotel" sub="Posizionamento, target, proposta di valore e tono di voce." />
      <Card className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <Field label="Posizionamento dichiarato" value={p.positioning.declared || "Non dichiarato nelle fonti lette: da confermare con la direzione"} />
        <Field label="Posizionamento inferito" value={p.positioning.inferred} />
        <Field label="Target dichiarato" value={p.target.declared} />
        <Field label="Target inferito" value={p.target.inferred} />
        <Field label="Target strategico" value={p.target.strategic} />
        <Field label="Proposta di valore" value={p.value_proposition} />
        <Field label="Tono di voce" value={p.tone_of_voice} />
        {p.commercial_pillars.length > 0 && (
          <div className="md:col-span-2">
            <div className="text-[11px] font-bold uppercase tracking-wide text-brand-muted mb-1.5">Pilastri commerciali</div>
            <div className="flex flex-wrap gap-2">
              {p.commercial_pillars.map((pl) => <span key={pl} className="px-3 py-1 rounded-full bg-brand-cream border border-brand-gold/30 text-[13px] text-brand-green font-medium">{pl}</span>)}
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}

/* ---------------- Actions ---------------- */
function Actions({ ccu, scope, reload, openExplain }) {
  return (
    <section className="mb-8">
      <SectionHead icon={Sparkles} title="Le tue azioni" sub="Ogni tua conferma o indicazione aggiorna subito la comprensione dell'hotel e crea una nuova versione tracciata." />
      <div className="flex flex-wrap gap-3">
        <StrategyDialog scope={scope} reload={reload} />
        <FindingDialog scope={scope} reload={reload} />
        <RecomputeButton scope={scope} reload={reload} />
        <Button variant="outline" onClick={() => openExplain(null)} data-testid="explainability-btn"
          className="h-11 rounded-lg border-brand-green/30 text-brand-green hover:bg-brand-green hover:text-brand-cream text-[13.5px] font-semibold">
          <Info size={16} className="mr-2" /> Explainability
        </Button>
      </div>
    </section>
  );
}

function StrategyDialog({ scope, reload }) {
  const [open, setOpen] = useState(false);
  const [seg, setSeg] = useState("");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!seg.trim()) return;
    setBusy(true);
    try {
      await api.injectDirector({
        tenant: scope.tenant, hotel: scope.hotel, kind: "DIRECTOR_DECLARED_STRATEGY",
        strategy_dimensions: { target_segment: seg.trim() }, strategy_summary: summary.trim() || undefined,
        description: "Strategia dichiarata dal direttore",
      });
      toast.success("Strategia dichiarata. Comprensione aggiornata.");
      setOpen(false); setSeg(""); setSummary(""); await reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="action-strategy" className="h-11 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[13.5px] font-semibold">
          <Target size={16} className="mr-2" /> Dichiara strategia
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Identità strategica desiderata</DialogTitle>
          <DialogDescription>Racconta dove vuoi portare l'hotel: aggiorna l'identità desiderata.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-[13px] font-semibold text-brand-ink">Target desiderato
            <Input data-testid="strategy-segment" value={seg} onChange={(e) => setSeg(e.target.value)} placeholder="Es. coppie premium internazionali" className="mt-1" />
          </label>
          <label className="block text-[13px] font-semibold text-brand-ink">Sintesi (opzionale)
            <Textarea data-testid="strategy-summary" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Es. riposizionare come boutique lifestyle upscale" className="mt-1" />
          </label>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="strategy-submit" className="bg-brand-green text-brand-cream">Dichiara e ricomputa</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FindingDialog({ scope, reload }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [pri, setPri] = useState("HIGH");
  const [mat, setMat] = useState("0.7");
  const [conf, setConf] = useState("0.65");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.injectFinding({
        tenant: scope.tenant, hotel: scope.hotel, title: title.trim(),
        priority_category: pri, materiality_score: Number(mat), conclusion_confidence: Number(conf),
      });
      toast.success("Segnalazione aggiunta. Comprensione aggiornata.");
      setOpen(false); setTitle(""); await reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="action-finding" variant="outline" className="h-11 rounded-lg border-brand-green/30 text-brand-green hover:bg-brand-green hover:text-brand-cream text-[13.5px] font-semibold">
          <Plus size={16} className="mr-2" /> Aggiungi segnalazione
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Nuova segnalazione</DialogTitle>
          <DialogDescription>Entra nella comprensione solo se è abbastanza importante (vedi "Cosa conta davvero").</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input data-testid="finding-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo della segnalazione" />
          <div className="grid grid-cols-3 gap-3">
            <label className="text-[12px] font-semibold text-brand-muted">Priorità
              <Select value={pri} onValueChange={setPri}>
                <SelectTrigger data-testid="finding-priority" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((x) => <SelectItem key={x} value={x}>{PRI[x].label}</SelectItem>)}</SelectContent>
              </Select>
            </label>
            <label className="text-[12px] font-semibold text-brand-muted">Importanza (0-1)
              <Input data-testid="finding-mat" value={mat} onChange={(e) => setMat(e.target.value)} className="mt-1" />
            </label>
            <label className="text-[12px] font-semibold text-brand-muted">Quanto ne sei sicuro (0-1)
              <Input data-testid="finding-conf" value={conf} onChange={(e) => setConf(e.target.value)} className="mt-1" />
            </label>
          </div>
          <p className="text-[11.5px] text-brand-muted italic">Sotto la soglia di importanza la segnalazione resta in archivio e non compare in Panoramica.</p>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="finding-submit" className="bg-brand-green text-brand-cream">Aggiungi e ricomputa</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecomputeButton({ scope, reload }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const before = (await api.getCCU(scope.tenant, scope.hotel)).understanding_version;
      const after = (await api.recompute(scope.tenant, scope.hotel, "MANUAL")).understanding_version;
      if (before === after) toast("Nessun cambiamento: la comprensione si aggiorna solo quando succede qualcosa di nuovo.", { icon: "♻️" });
      else toast.success(`Nuova versione v${after}.`);
      await reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Button data-testid="action-recompute" onClick={run} disabled={busy} variant="outline"
      className="h-11 rounded-lg border-brand-green/30 text-brand-green hover:bg-brand-green hover:text-brand-cream text-[13.5px] font-semibold">
      <GitCompare size={16} className="mr-2" /> Ricomputa (idempotente)
    </Button>
  );
}

/* ---------------- Provenance ---------------- */
function Provenance({ ccu }) {
  const snap = ccu.input_snapshot_ids || {};
  const chips = [
    ["metodologia", ccu.methodology_version], ["knowledge", ccu.knowledge_version],
    ["software", ccu.software_version], ["regole", ccu.precedence_rules_version],
    ["evidence", (snap.evidence || []).length], ["findings", (snap.findings || []).length],
    ["director", (snap.director || []).length],
  ];
  return (
    <div className="border-t border-brand-gold/30 pt-4 mb-4 flex flex-wrap gap-x-5 gap-y-1 text-[11.5px] font-mono text-brand-muted">
      {chips.map(([k, v]) => <span key={k}>{k}: <span className="text-brand-ink">{String(v)}</span></span>)}
    </div>
  );
}

/* ---------------- Explain Sheet ---------------- */
function ExplainSheet({ ccu, state, onOpenChange }) {
  const rules = ccu.precedence_rules_applied.filter((r) => !state.field || r.field === state.field);
  return (
    <Sheet open={state.open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto leutelia-scroll">
        <SheetHeader>
          <SheetTitle className="font-serif text-xl">Explainability</SheetTitle>
        </SheetHeader>
        <p className="text-[12.5px] text-brand-muted mt-1">
          Come LEUTELIA ha scelto tra fonti diverse
          {state.field && <> · campo <span className="font-mono">{state.field}</span></>}
        </p>
        <div className="mt-4 space-y-3">
          {rules.length === 0 && <div className="text-[13px] text-brand-muted italic">Nessuna regola di precedenza applicata (nessun conflitto in questo campo).</div>}
          {rules.map((r, i) => (
            <div key={i} className="rounded-lg border border-brand-gold/25 p-4" data-testid={`rule-${r.field}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] text-brand-green">{r.rule_id}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold border ${r.outcome === "CONTRADICTED" ? "bg-red-100 text-red-700 border-red-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"}`}>{r.outcome}</span>
              </div>
              <div className="text-[13.5px] font-semibold text-brand-ink mt-1 capitalize">{r.field.replace(/_/g, " ")}</div>
              <p className="text-[12.5px] text-brand-muted mt-1">{r.reason}</p>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] font-mono">
                <div><span className="text-emerald-700 font-semibold">scelte:</span> {r.chosen_evidence_ids.join(", ") || "—"}</div>
                <div><span className="text-red-700 font-semibold">scartate:</span> {r.discarded_evidence_ids.join(", ") || "—"}</div>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ---------------- shared ---------------- */
function SectionHead({ icon: Icon, title, sub, inline }) {
  return (
    <div className={`flex items-center gap-2.5 ${inline ? "" : "mb-3"}`}>
      <Icon size={20} className="text-brand-gold" strokeWidth={1.9} />
      <div>
        <h3 className="font-serif text-[20px] text-brand-ink leading-none">{title}</h3>
        {sub && <p className="text-[12.5px] text-brand-muted mt-1">{sub}</p>}
      </div>
    </div>
  );
}
